import { useEffect, useRef, type ReactNode } from 'react';
import { warnOnce } from '@/lib/warnOnce';
import { connectSignageMqtt } from '@/mqtt/client';
import { buildMetaTopics, initPreventer, startTopicAccessCheck } from '@/mqtt/preventer';
import { PREVIEW_MODE } from '@/lib/previewMode';
import { PreviewModeProvider } from '@/mqtt/PreviewModeProvider';
import { useConnectGate, useSignageStore } from '@/store/signageStore';

/**
 * The connect gate, as a React component. Replaces both legacy Vuex plugins' lifecycle
 * (`checkAndConnect` in `store/index.ts`, docs/MIGRATION.md §5).
 *
 * ```
 * mount                                    -> initPreventer()  -> setPreventerLoaded(token)
 * ModuleLoader mount (SYM-491)                                 -> setModuleLoaded()
 * widgets mount     (SYM-490 useWidgetData)                    -> registerTopic(...)
 *
 * gate: preventerLoaded && moduleLoaded && topicCount > 0
 *   -> savemeta  -> metaEnabled -> setCanConnectMqtt()  -> mqtt connect + subscribe
 * ```
 *
 * All three gate inputs are asynchronous and arrive in genuinely unpredictable order — the
 * preventer script is a third-party `async` load, `moduleLoaded` depends on render, and the
 * topic count depends on how many widgets a config happens to carry. So the gate is
 * *derived* from the three, never latched by whichever finishes last.
 *
 * ## Why refs and `getState()` rather than props and selectors
 *
 * Everything below runs outside render. The store is read imperatively so a message arriving
 * for one widget does not re-render this provider (and with it, nothing — but the habit is
 * what keeps it that way). Only the two booleans that *drive* the effects are subscribed to.
 *
 * The `children` element identity is stable across this component's re-renders, so React
 * bails out of re-rendering the widget tree when the gate flips.
 */
export function LiveMqttProvider({ children }: { children?: ReactNode }) {
  const gate = useConnectGate();
  const canConnectMqtt = useSignageStore((s) => s.canConnectMqtt);

  const accessRequested = useRef(false);
  const connectStarted = useRef(false);

  /* ---- 1. token ------------------------------------------------------------------------ */

  useEffect(
    () =>
      initPreventer((token) => {
        useSignageStore.getState().setPreventerLoaded(token);
      }),
    [],
  );

  /* ---- 2. gate opens -> savemeta ------------------------------------------------------- */

  /**
   * Fires `startTopicAccessCheck` exactly once.
   *
   * Two guards, and both are needed. The dependency is the *boolean*, not the gate object,
   * so a fifth widget registering a topic after the gate opened cannot re-run this effect
   * and cancel an in-flight retry chain. The ref then makes it exactly-once even if a
   * dependency changes for some other reason — and it is reset in the cleanup, because
   * StrictMode's mount/unmount/mount cycle keeps refs alive across the remount and a guard
   * that survived it would leave the display permanently unconnected in dev.
   */
  useEffect(() => {
    if (!gate.canConnect) return;
    if (accessRequested.current) return;
    accessRequested.current = true;

    const { topicItems, baseTopic, floorCode } = useSignageStore.getState();
    const topics = buildMetaTopics({ topics: topicItems.keys(), baseTopic, floorCode });

    console.info(
      '[mqtt] gate open (preventer + module + %d topic(s)) — asking the preventer for access',
      topicItems.size,
    );

    const cancel = startTopicAccessCheck({
      token: gate.token,
      topics,
      onGranted: () => {
        useSignageStore.getState().setCanConnectMqtt();
      },
    });

    return () => {
      cancel();
      accessRequested.current = false;
    };
  }, [gate.canConnect, gate.token]);

  /* ---- 3. access granted -> connect ---------------------------------------------------- */

  useEffect(() => {
    if (!canConnectMqtt) return;
    if (connectStarted.current) return;

    const { mqttUrl, token, baseTopic, floorCode, heartBeat, topicItems } =
      useSignageStore.getState();

    if (!mqttUrl) {
      // `loadConfig` already warned; this is the point where it stops being cosmetic.
      console.error(
        '[mqtt] no broker url — the config has no `mqttUrl` and VITE_MQTT_URL is unset. ' +
          'This display will show placeholder data forever.',
      );
      return;
    }

    connectStarted.current = true;
    const handle = connectSignageMqtt({
      url: mqttUrl,
      token,
      baseTopic,
      floorCode,
      heartBeatMs: heartBeat,
      // Snapshot: this is the list the broker was asked to authorise, so it is the list we
      // subscribe with. `resolveComponentId` below stays live for routing.
      topics: [...topicItems.keys()],
      resolveComponentId: (topic) => useSignageStore.getState().topicItems.get(topic),
      deliver: (componentId, payload) => {
        useSignageStore.getState().deliverMessage(componentId, payload);
      },
      onConnectedChange: (connected) => {
        useSignageStore.getState().setMqttConnected(connected);
      },
      reload: () => {
        window.location.reload();
      },
    });

    return () => {
      handle.stop();
      connectStarted.current = false;
    };
  }, [canConnectMqtt]);

  /* ---- 4. the config that can never connect -------------------------------------------- */

  /**
   * A config whose widgets are all static (`DateWidget`, `Logo`, `ImageLabelWidget`,
   * `ProgressBar`) registers no topics, so the gate never opens and nothing ever connects.
   * That is correct — there is nothing to subscribe to — but from the outside it is
   * indistinguishable from a hang, so it says so once.
   */
  const noTopics = gate.moduleLoaded && gate.topicCount === 0;
  useEffect(() => {
    if (!noTopics) return;
    warnOnce(
      'mqtt-no-topics',
      '[mqtt] no topics registered; not connecting. Every widget in this config is static — ' +
        'if that is a surprise, check `options.topic` in the config.',
    );
  }, [noTopics]);

  return <>{children}</>;
}

/**
 * `PREVIEW_MODE` folds to a literal at build time, so a preview bundle has no reachable
 * path into the preventer or the client — see `@/lib/previewMode` for why that is chosen at
 * build time rather than runtime.
 *
 * Selecting the component here, rather than branching inside one, keeps every hook
 * unconditional in both implementations.
 */
export const MqttProvider = PREVIEW_MODE ? PreviewModeProvider : LiveMqttProvider;
